/** Task clocks come from current human instructions, never model defaults. */
type Message={role:string;content:unknown};
type Payload={mode:string;tool?:string;arguments?:Record<string,unknown>};
export type TaskTimeIssue={error:'task_time_required'|'task_time_mismatch'|'task_time_reference_required';question:string;instruction:string};
const units=['zero','una','due','tre','quattro','cinque','sei','sette','otto','nove','dieci','undici','dodici','tredici','quattordici','quindici','sedici','diciassette','diciotto','diciannove','venti','ventuno','ventidue','ventitre'];
function number(t:string):number|null{
 if(/^\d{1,2}$/.test(t))return +t;
 if(t==='un'||t==='uno')return 1;
 const n=units.indexOf(t);if(n>=0)return n;
 for(const [i,base] of ['venti','trenta','quaranta','cinquanta'].entries()){
  if(t===base)return (i+2)*10;
  for(let u=1;u<10;u++)if(t===(u===1||u===8?base.slice(0,-1):base)+(u===1?'uno':units[u]))return (i+2)*10+u;
 }
 return null;
}
function clocks(t:string):number[]{
 const found:number[]=[];const covered:Array<[number,number]>=[];
 const add=(h:number,m:number)=>{if(h>=0&&h<=23&&m>=0&&m<=59)found.push(h*60+m);};
 for(const x of t.matchAll(/\b(?:alle|all['’]|ore)\s*([a-z]+|\d{1,2})(?:\s*(?::|\.|e)\s*(un quarto|[a-z]+|\d{1,2}))?(?:\s+(?:del|di)\s+(pomeriggio|sera|mattina))?/g)){
  covered.push([x.index!,x.index!+x[0].length]);
  let h=number(x[1]);const m=x[2]==='mezza'||x[2]==='mezzo'?30:x[2]==='un quarto'?15:x[2]?number(x[2]):0;
  if(h===null||m===null)continue;
  if(x[3]&&x[3]!=='mattina'&&h<12)h+=12;
  add(h,m);
 }
 // Unanchored dotted numbers may be dates (20.12), never clock evidence.
 for(const x of t.matchAll(/\b(\d{1,2}):(\d{2})\b/g))if(!covered.some(([a,b])=>x.index!>=a&&x.index!<b))add(+x[1],+x[2]);
 if(/\bmezzogiorno\b/.test(t))add(12,0);
 if(/\bmezzanotte\b/.test(t))add(0,0);
 return [...new Set(found)];
}

type TaskClockReference={id:string;title:string;due_date:string;project_id:string;project_name:string};
const uuid=(v:unknown):v is string=>typeof v==='string'&&/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(v);
function minuteInZone(due:string,timeZone:string):number|null{
 try{
  const parts=new Intl.DateTimeFormat('it-IT',{timeZone,hour:'2-digit',minute:'2-digit',hourCycle:'h23'}).formatToParts(new Date(due));
  return Number(parts.find(p=>p.type==='hour')?.value)*60+Number(parts.find(p=>p.type==='minute')?.value);
 }catch{return null;}
}
function normalized(text:string){return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'');}
const acknowledgement=/^(?:si(?:,? procedi)?|ok|va bene|procedi|continua|confermo)[.! ]*$/;
function sectionAnswer(bounded:Message[],index:number,source:string,title:string):boolean{
 const previous=bounded[index-1];if(previous?.role!=='assistant'||typeof previous.content!=='string')return false;
 const question=normalized(previous.content),slot=question.match(/\b(?:quale|che)\s+sezione\b[\s\S]{0,1000}/)?.[0];
 if(!slot?.includes('?')||!question.includes(title))return false;
 const answer=source.trim().replace(/[.!?]+$/,'').trim();if(!answer||answer.length>80)return false;
 const labels=[...slot.matchAll(/["«“]([^"»”\n]{1,80})["»”]/g)].map(m=>m[1].trim());
 if(labels.includes(answer))return true;
 // Spoken transcripts need not put quotation marks around the offered labels.
 const escaped=answer.replace(/[.*+?^{}$()|[\]\\]/g,'\\$&');
 return labels.length===0&&new RegExp('(?:^|\\W)'+escaped+'(?=\\W|$)').test(slot);
}
function projectReferenceAnswer(bounded:Message[],index:number,source:string):boolean{
 if(!/\bprogett[oi]\b/.test(source)||clocks(source).length)return false;
 // A new task, paused configuration, or read-only topic is not a missing reference.
 if(/\b(?:task|attivita|cambiamo|passiamo|altr[oa]\s+lavoro|nuov[oa]\s+lavoro)\b/.test(source)||/\bnon\s+(?:prepar\w*|cre\w*|salv\w*|modific\w*|aggiung\w*)\b/.test(source)||/\b(?:solo|soltanto)\s+(?:lettur\w*|legger\w*|veder\w*|elenc\w*)\b/.test(source))return false;
 const previous=bounded[index-1];if(previous?.role!=='assistant'||typeof previous.content!=='string')return false;
 const question=normalized(previous.content);
 if(!question.includes('?')||!/\bprogett[oi]\b/.test(question)||!/\b(?:nome|quale|chiama|parola|ricerca)\b/.test(question))return false;
 return /\bprogetto\s+(?:si\s+chiama|e\b)|\b(?:leggi|mostra|elenca|cerca|cercalo)\b[^.!?]{0,100}\bprogett[oi]\b/.test(source);
}
function schedulingTurn(messages:Message[],payload:Payload):string{
 const bounded=messages.slice(-32),users=bounded.map((m,index)=>({...m,index})).filter(m=>m.role==='user'&&typeof m.content==='string').slice(-12);
 let i=users.length-1;
 if(i<0)return '';
 while(i>0&&acknowledgement.test(normalized(String(users[i].content)).trim()))i--;
 const current=normalized(String(users[i].content)).trim();
 const title=typeof payload.arguments?.title==='string'?normalized(payload.arguments.title).trim():'';
 // Explicit current clock answers always take precedence, including a correction.
 if(clocks(current).length)return current;
 if(title.length<3){
  let j=i;while(j>0&&acknowledgement.test(normalized(String(users[j].content)).trim()))j--;
  return normalized(String(users[j].content));
 }
 for(let j=i;j>=0&&i-j<8;j--){
  const source=normalized(String(users[j].content)).trim();
  if(acknowledgement.test(source))continue;
  // The latest substantive request naming this task wins, including one with no
  // hour. Never skip it to borrow an older clock for a new task with the same name.
  if(source.includes(title))return source;
  const membershipBridge=/\b(?:aggiunt\w*|aggiungere|accesso)\b/.test(source)&&/\b(?:collega|progetto)\b/.test(source)&&/\b(?:prima di confermare|gia descritt\w*|poi proseguiamo)\b/.test(source)&&!/\b(?:altr[oa]|invece|nuov[oa])\b/.test(source)&&clocks(source).length===0;
  if(membershipBridge||sectionAnswer(bounded,users[j].index,source,title)||projectReferenceAnswer(bounded,users[j].index,source))continue;
  // Only known reference/section answers can bridge to the original human task.
  // Assistant statements never supply its time, and an unrelated human turn stops.
  return current;
 }
 return current;
}
export function createTaskClockEvidence(scope:string){
 const org=scope.split(':')[0],rows=new Map<string,TaskClockReference>();
 return {
  clear(){rows.clear();},
  remember(payload:Payload,output:any,ok=true){
   // Only the current scoped native exact reader is evidence. Lists, errors,
   // proposals, quoted history and assistant text cannot populate this cache.
   if(payload.mode==='request'&&payload.tool==='crm_tasks_get'&&typeof payload.arguments?.task_id==='string')rows.delete(payload.arguments.task_id);
   if(!ok||!uuid(org)||payload.mode!=='request'||payload.tool!=='crm_tasks_get'||!uuid(payload.arguments?.task_id)||output?.status!=='read'||output.result?.success!==true)return;
   const row=output.result.data;
   if(row?.id!==payload.arguments.task_id||!uuid(row.project_id)||row.project?.id!==row.project_id||row.project.organization_id!==org||typeof row.title!=='string'||!row.title.trim()||row.title.length>300||typeof row.due_date!=='string'||!Number.isFinite(Date.parse(row.due_date)))return;
   rows.set(row.id,{id:row.id,title:row.title,due_date:row.due_date,project_id:row.project_id,project_name:typeof row.project.name==='string'?row.project.name:''});
   if(rows.size>12)rows.delete(rows.keys().next().value!);
  },
  issue(payload:Payload,messages:Message[],timeZone='Europe/Rome',referenceTaskId?:string):TaskTimeIssue|null{
   return taskTimeGrounding(payload,messages,timeZone,{references:[...rows.values()],referenceTaskId});
  }
 };
}
export function taskTimeGrounding(payload:Payload,messages:Message[],timeZone='Europe/Rome',evidence:{references?:TaskClockReference[];referenceTaskId?:string}={}):TaskTimeIssue|null{
 if(payload.mode!=='request'||payload.tool!=='crm_tasks_create')return null;
 const due=payload.arguments?.due_date;
 if(typeof due!=='string'||!/^\d{4}-\d{2}-\d{2}T/.test(due)||!Number.isFinite(Date.parse(due)))return null;
 const fullText=schedulingTurn(messages,payload);
 // Separate an old task from a subsequent creation even without the word "poi".
 const text=fullText.split(/\b(?:poi|dopodiche|invece|quindi)\b|[;]\s*|(?:,\s*|\be\s+)(?=(?:crea(?:mi|ne)?|pianifica(?:mi)?|programma(?:mi)?|prepara(?:mi)?|fissa(?:mi)?|aggiungi(?:mi)?|la nuova attivita)\b)/).at(-1)||'';
 const reference=/\b(?:stessa ora|stesso orario|come prima|solita ora)\b/.test(text);
 let times=clocks(text);
 if(reference){
  const project=payload.arguments?.project_id;
  const sources=(evidence.references||[]).filter(r=>(project===r.project_id||typeof project==='string'&&normalized(project)===normalized(r.project_name))&&(r.id===evidence.referenceTaskId||fullText.includes(r.id.toLowerCase())||fullText.includes(normalized(r.title))));
  if(sources.length===1){const minute=minuteInZone(sources[0].due_date,timeZone);times=minute===null?[]:[minute];}
  else{
   // The verified UI receipt identifies a source, but does not substitute its
   // clock for a fresh permission-checked read in this request/session.
   const ids=[...fullText.matchAll(/\b[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\b/g)].map(x=>x[0]);
   const id=uuid(evidence.referenceTaskId)?evidence.referenceTaskId:ids.length===1?ids[0]:null;
   const question=sources.length>1?'Di quale attività vuoi mantenere l’orario?':'Quale orario vuoi mantenere per la nuova attività?';
   return {error:'task_time_reference_required',question,instruction:id?'Prima di proporre la nuova task cerca crm_tasks_get e leggi la task '+id+' con i permessi correnti. Usa solo l’ora letta se il progetto coincide. Nessuna proposta è stata preparata.':question+' Se l’attività di riferimento è già nominata, risolvila e leggila con crm_tasks_get. Non scegliere arbitrariamente l’ultima attività letta. Nessuna proposta è stata preparata.'};
  }
 }
 const question=times.length>1?'A che ora vuoi fissare la nuova attività? Hai indicato più orari.':'A che ora vuoi fissare la nuova attività?';
 if(times.length!==1)return {error:'task_time_required',question,instruction:question+' Non è stata preparata alcuna proposta. Non usare orari predefiniti, di altre attività o suggeriti dall’assistente.'};
 const actual=minuteInZone(due,timeZone);
 if(actual===null)return {error:'task_time_required',question,instruction:'Fuso orario non verificato. Chiedi orario e fuso senza preparare una proposta.'};
 if(actual!==times[0]){
  const clock=String(Math.floor(times[0]/60)).padStart(2,'0')+':'+String(times[0]%60).padStart(2,'0');
  return {error:'task_time_mismatch',question:'Vuoi fissare la nuova attività alle '+clock+'?',instruction:'Nessuna proposta preparata: due_date non coincide con le '+clock+(reference?' lette dall’attività di riferimento':' dichiarate dall’utente')+' nel fuso '+timeZone+'. Correggi solo l’ora usando quel dato. La proposta resta da confermare.'};
 }
 return null;
}
